import { useCallback, useEffect, useRef, useState } from 'react'

import { authFetch } from '../lib/auth'
import type {
  PromptCacheAggregate,
  RecentRunSummary,
  SubconAdminSnapshot,
  SubconAvailability,
} from '../lib/subconAdmin'

const REFRESH_MS = 60_000

function compactId(value?: string) {
  if (!value) return '—'
  return value.length > 18 ? `${value.slice(0, 8)}…${value.slice(-6)}` : value
}

function fmtNumber(value?: number) {
  return value === undefined ? '未知' : value.toLocaleString('en-US')
}

function fmtRatio(value?: number) {
  return value === undefined ? '未知' : `${Math.round(value * 100)}%`
}

function fmtDuration(ns?: number) {
  if (ns === undefined) return '未知'
  const ms = ns / 1_000_000
  if (ms < 1000) return `${Math.round(ms)}ms`
  return `${(ms / 1000).toFixed(ms < 10_000 ? 1 : 0)}s`
}

function fmtTime(value?: string) {
  if (!value) return '未知'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.valueOf())) return value
  return parsed.toLocaleString('ja-JP', {
    timeZone: 'Asia/Tokyo',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function availabilityLabel(availability: SubconAvailability) {
  if (availability === 'available') return '可用'
  if (availability === 'unreachable') return '不可达'
  return '读取异常'
}

function activityLabel(activity: string) {
  if (activity === 'running') return 'running'
  if (activity === 'idle') return 'idle'
  if (activity === 'unreachable') return 'unreachable'
  return 'error'
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="whisper-metric">
      <span className="whisper-metric-label">{label}</span>
      <strong>{value}</strong>
    </div>
  )
}

function SectionState({ availability }: { availability: SubconAvailability }) {
  return <span className={`whisper-section-state ${availability}`}>{availabilityLabel(availability)}</span>
}

function CacheSummary({ aggregate }: { aggregate?: PromptCacheAggregate }) {
  if (!aggregate) return <div className="whisper-empty">cache telemetry 未知</div>
  return (
    <div className="whisper-metric-grid">
      <Metric label="命中率" value={fmtRatio(aggregate.cachedInputRatio)} />
      <Metric label="step coverage" value={fmtRatio(aggregate.coverageRatio)} />
      <Metric label="token coverage" value={fmtRatio(aggregate.tokenCoverageRatio)} />
      <Metric label="cached input" value={fmtNumber(aggregate.cachedInputTokens)} />
      <Metric label="cache write" value={fmtNumber(aggregate.cacheWriteTokens)} />
      <Metric label="eligible steps" value={fmtNumber(aggregate.eligibleSteps)} />
    </div>
  )
}

function RunRow({ run }: { run: RecentRunSummary }) {
  const usage = run.usage
  const tools = run.tools.identifiers.length ? run.tools.identifiers.join(' · ') : '无 / 未知'
  return (
    <div className="whisper-run-row">
      <div className="whisper-run-head">
        <strong>{compactId(run.runId)}</strong>
        <span>{run.status || 'status 未知'}</span>
      </div>
      <div className="whisper-run-meta">
        {fmtTime(run.createdAt)} · duration {fmtDuration(run.durationNs)} · TTFT {fmtDuration(run.ttftNs)}
      </div>
      <div className="whisper-run-meta">
        tokens {fmtNumber(usage.totalTokens)} · prompt {fmtNumber(usage.promptTokens)} · cache {fmtNumber(usage.cachedInputTokens)} · steps {fmtNumber(run.stepCount)}
      </div>
      <div className="whisper-tool-line">tools · {tools}</div>
    </div>
  )
}

export function WhisperPanelView({
  snapshot,
  loading,
  error,
  stale,
  onRefresh,
}: {
  snapshot: SubconAdminSnapshot | null
  loading: boolean
  error: string | null
  stale: boolean
  onRefresh?: () => void
}) {
  if (!snapshot) {
    return (
      <section className="whisper-panel" aria-label="Whisper Subconscious 状态">
        <div className="whisper-panel-head">
          <div>
            <div className="whisper-title">🫧 Whisper</div>
            <div className="whisper-subtitle">Subconscious admin</div>
          </div>
          {onRefresh && <button className="whisper-refresh" type="button" onClick={onRefresh}>↻</button>}
        </div>
        <div className={`whisper-hero ${error ? 'unreachable' : ''}`}>
          <span className="whisper-status-dot" />
          <div>
            <strong>{loading ? '正在读取 Subcon…' : 'Subcon 不可达'}</strong>
            <div>{error || '还没有可用的 admin snapshot'}</div>
          </div>
        </div>
      </section>
    )
  }

  const runtime = snapshot.runtime
  const overview = runtime.overview
  const context = overview.context
  const provider = overview.providerUsage
  const memory = snapshot.relationshipMemory.summary
  const isDegraded = runtime.availability !== 'available' || Boolean(error)
  const kinds = Object.entries(memory.countsByKind).filter(([, count]) => count > 0)

  return (
    <section className="whisper-panel" aria-label="Whisper Subconscious 状态">
      <div className="whisper-panel-head">
        <div>
          <div className="whisper-title">🫧 Whisper</div>
          <div className="whisper-subtitle">Subconscious admin</div>
        </div>
        <button className={`whisper-refresh ${loading ? 'loading' : ''}`} type="button" onClick={onRefresh} aria-label="刷新 Whisper">
          ↻
        </button>
      </div>

      <div className={`whisper-hero ${overview.activity} ${isDegraded ? 'degraded' : ''}`}>
        <span className="whisper-status-dot" />
        <div className="whisper-hero-copy">
          <div className="whisper-hero-title">
            <strong>{activityLabel(overview.activity)}</strong>
            {stale && <span className="whisper-stale">stale</span>}
          </div>
          <div>
            {overview.agentName || 'Subconscious'} · {overview.model || overview.modelHandle || 'model 未知'}
          </div>
          <div className="whisper-observed">observed {fmtTime(overview.observedAt)}</div>
        </div>
      </div>

      {error && <div className="whisper-warning">最近刷新失败，保留上一次成功 snapshot · {error}</div>}

      <div className="whisper-metric-grid whisper-overview-grid">
        <Metric label="context" value={context ? `${fmtNumber(context.currentTokens)} / ${fmtNumber(context.maxTokens)}` : '未知'} />
        <Metric label="利用率" value={context ? fmtRatio(context.utilizationRatio) : '未知'} />
        <Metric label="active runs" value={fmtNumber(overview.activeRunIds.length)} />
        <Metric label="memory" value={fmtNumber(memory.effectiveMemoryCount)} />
      </div>

      <details className="whisper-details" open>
        <summary>
          <span>运行 / Context</span>
          <SectionState availability={runtime.availability} />
        </summary>
        <div className="whisper-detail-body">
          <div className="whisper-kv"><span>agent</span><strong>{overview.agentName || '未知'} · {compactId(snapshot.agentId)}</strong></div>
          <div className="whisper-kv"><span>model handle</span><strong>{overview.modelHandle || '未知'}</strong></div>
          <div className="whisper-kv"><span>provider</span><strong>{overview.providerCategory || '未知'}</strong></div>
          <div className="whisper-kv"><span>configured limit</span><strong>{fmtNumber(overview.configuredContextLimit)}</strong></div>
          {context ? (
            <>
              <div className="whisper-metric-grid">
                <Metric label="messages" value={fmtNumber(context.messageCount)} />
                <Metric label="archival" value={fmtNumber(context.archivalCount)} />
                <Metric label="recall" value={fmtNumber(context.recallCount)} />
              </div>
              <div className="whisper-token-sections">
                {Object.entries(context.sectionTokens).map(([name, value]) => (
                  <div className="whisper-token-row" key={name}><span>{name}</span><strong>{fmtNumber(value)}</strong></div>
                ))}
              </div>
            </>
          ) : <div className="whisper-empty">context telemetry 未知 / unavailable</div>}
          <div className="whisper-kv"><span>last run</span><strong>{compactId(overview.lastRun?.id)} · {overview.lastRun?.status || '未知'}</strong></div>
          <div className="whisper-kv"><span>stop reason</span><strong>{overview.lastRun?.stopReason || '未知'}</strong></div>
        </div>
      </details>

      <details className="whisper-details">
        <summary>
          <span>最近运行</span>
          <SectionState availability={snapshot.recentRuns.availability} />
        </summary>
        <div className="whisper-detail-body">
          {snapshot.recentRuns.items.length
            ? snapshot.recentRuns.items.map((run) => <RunRow key={run.runId} run={run} />)
            : <div className="whisper-empty">没有 recent run 数据；这不等于 0 次运行。</div>}
        </div>
      </details>

      <details className="whisper-details">
        <summary>
          <span>Prompt Cache</span>
          <SectionState availability={snapshot.promptCache.availability} />
        </summary>
        <div className="whisper-detail-body">
          <CacheSummary aggregate={snapshot.promptCache.aggregate} />
          <div className="whisper-kv"><span>conversation samples</span><strong>{fmtNumber(snapshot.promptCache.conversations.length)}</strong></div>
        </div>
      </details>

      <details className="whisper-details">
        <summary>
          <span>Provider usage</span>
          <span className={`whisper-quality ${provider.quality}`}>{provider.quality}</span>
        </summary>
        <div className="whisper-detail-body">
          <div className="whisper-kv"><span>source</span><strong>{provider.source}</strong></div>
          <div className="whisper-kv"><span>observed</span><strong>{fmtTime(provider.observedAt)}</strong></div>
          <div className="whisper-metric-grid">
            <Metric label="prompt" value={fmtNumber(provider.promptTokens)} />
            <Metric label="completion" value={fmtNumber(provider.completionTokens)} />
            <Metric label="total" value={fmtNumber(provider.totalTokens)} />
            <Metric label="remaining" value={provider.remaining === undefined ? '未知' : `${fmtNumber(provider.remaining)}${provider.unit ? ` ${provider.unit}` : ''}`} />
          </div>
        </div>
      </details>

      <details className="whisper-details">
        <summary>
          <span>Relationship memory</span>
          <SectionState availability={snapshot.relationshipMemory.availability} />
        </summary>
        <div className="whisper-detail-body">
          <div className="whisper-metric-grid">
            <Metric label="genesis" value={fmtNumber(memory.genesisMemoryCount)} />
            <Metric label="effective" value={fmtNumber(memory.effectiveMemoryCount)} />
            <Metric label="active" value={fmtNumber(memory.activeMemoryCount)} />
            <Metric label="inactive" value={fmtNumber(memory.inactiveMemoryCount)} />
            <Metric label="corrected" value={fmtNumber(memory.ownerCorrectedCount)} />
            <Metric label="revisions" value={fmtNumber(memory.ownerRevisionCount)} />
          </div>
          <div className="whisper-kv"><span>latest correction</span><strong>{fmtTime(memory.latestOwnerRevisionAt)}</strong></div>
          <div className="whisper-kind-list">
            {kinds.length
              ? kinds.map(([kind, count]) => <span key={kind}>{kind} · {count}</span>)
              : <span>kind counts 全为 0</span>}
          </div>
          <div className="whisper-privacy-note">状态面板只取 summary/count metadata；不会轮询 memory body、transcript、prompt 或 tool payload。</div>
        </div>
      </details>
    </section>
  )
}

export function WhisperPanel({ active }: { active: boolean }) {
  const [snapshot, setSnapshot] = useState<SubconAdminSnapshot | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const hasSnapshotRef = useRef(false)

  const refresh = useCallback(async () => {
    if (!active) return
    setLoading(true)
    try {
      const response = await authFetch('/api/subcon/admin/snapshot')
      if (!response.ok) throw new Error(`admin ${response.status}`)
      const next = await response.json() as SubconAdminSnapshot
      setSnapshot(next)
      hasSnapshotRef.current = true
      setError(null)
      setStale(false)
    } catch {
      setError('Whisper admin 暂时不可达')
      setStale(hasSnapshotRef.current)
    } finally {
      setLoading(false)
    }
  }, [active])

  useEffect(() => {
    if (!active) return
    void refresh()
    const timer = window.setInterval(() => void refresh(), REFRESH_MS)
    return () => window.clearInterval(timer)
  }, [active, refresh])

  return (
    <WhisperPanelView
      snapshot={snapshot}
      loading={loading}
      error={error}
      stale={stale}
      onRefresh={() => void refresh()}
    />
  )
}
